import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';

interface NmInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  hint?: string;
}

/** Neumorphic inset text input with label, icon, error state, and password toggle. */
export function NmInput({
  label,
  error,
  icon,
  hint,
  type = 'text',
  className = '',
  id,
  ...props
}: NmInputProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
  const inputType = type === 'password' && showPassword ? 'text' : type;

  return (
    <div className={`nm-input-wrap ${className}`}>
      {label && (
        <label htmlFor={inputId} className="nm-input-label">
          {label}
        </label>
      )}
      <div className="nm-input-inner">
        {icon && <span className="nm-input-icon">{icon}</span>}
        <input
          id={inputId}
          type={inputType}
          className={`nm-input-field${icon ? ' has-icon-left' : ''}${type === 'password' ? ' has-password-toggle' : ''}`}
          {...props}
        />
        {type === 'password' && (
          <button
            type="button"
            className="nm-input-toggle"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={t(showPassword ? 'auth.hidePassword' : 'auth.showPassword')}
            aria-pressed={showPassword}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </div>
      {error && <span className="nm-input-error">{error}</span>}
      {hint && !error && (
        <span className="caption" style={{ marginTop: 2 }}>
          {hint}
        </span>
      )}
    </div>
  );
}

function EyeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
